import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Save, LogOut, Inbox } from "lucide-react";
import { API } from "../lib/content";

const COLLECTIONS = [
  { key: "projects", label: "Projects" },
  { key: "posts", label: "Blog Posts" },
  { key: "testimonials", label: "Testimonials" },
  { key: "metrics", label: "Metrics" },
  { key: "services", label: "Services" },
  { key: "resources", label: "Resources" },
  { key: "speaking", label: "Speaking" },
  { key: "faqs", label: "FAQs" },
  { key: "achievements", label: "Achievements" },
  { key: "education", label: "Education" },
  { key: "submissions", label: "Inbox" },
];

const ax = axios.create({ baseURL: API, withCredentials: true });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await ax.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 space-y-4">
        <h1 className="font-display text-2xl font-medium tracking-tight">Admin Panel</h1>
        <p className="text-sm text-muted-foreground">Sign in to manage site content.</p>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="w-full h-11 px-4 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="w-full h-11 px-4 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50" />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-500">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit"
          className="w-full h-11 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-60">
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const FIELD_HINTS = {
  projects: { title: "", category: "", problem: "", approach: "", technology: [], outcome: "", impact: "", featured: true, order: 1 },
  posts: { title: "", category: "AI", excerpt: "", read_time: "5 min read", date: "2026-06-01", order: 1 },
  testimonials: { quote: "", name: "", role: "", company: "", order: 1 },
  metrics: { value: 0, suffix: "+", label: "", detail: "", order: 1 },
  services: { icon: "Compass", title: "", description: "", order: 1 },
  resources: { type: "Guide", title: "", description: "", order: 1 },
  speaking: { type: "Conference", title: "", topic: "", date: "", location: "", order: 1 },
  faqs: { question: "", answer: "", order: 1 },
  achievements: { stat: "", title: "", detail: "", order: 1 },
  education: { degree: "", school: "", year: "", link: "", order: 1 },
};

const LONG_FIELDS = ["content", "message", "answer", "quote", "problem", "approach", "excerpt", "description", "detail", "outcome", "topic"];

const ItemEditor = ({ collection, item, onSaved, onDeleted }) => {
  const [data, setData] = useState(() => {
    const d = {};
    Object.entries(item).forEach(([k, v]) => { d[k] = Array.isArray(v) ? v.join(", ") : v; });
    return d;
  });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!item.id);

  const authHeader = () => {
    const t = localStorage.getItem("admin_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.entries(data).forEach(([k, v]) => {
        if (Array.isArray(item[k])) payload[k] = String(v).split(",").map((s) => s.trim()).filter(Boolean);
        else payload[k] = v;
      });
      if (item.id) {
        await ax.put(`/admin/${collection}/${item.id}`, { data: payload }, { headers: authHeader() });
      } else {
        await ax.post(`/admin/${collection}`, { data: payload }, { headers: authHeader() });
      }
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!item.id || !window.confirm("Delete this item?")) return;
    try {
      await ax.delete(`/admin/${collection}/${item.id}`, { headers: authHeader() });
      toast.success("Deleted");
      onDeleted();
    } catch {
      toast.error("Delete failed");
    }
  };

  const label = data.title || data.question || data.name || data.label || "New item";
  const inputCls = "w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50";

  return (
    <div className="rounded-xl border border-border bg-card">
      <button
        onClick={() => setOpen(!open)}
        data-testid="admin-item-toggle"
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="font-display text-sm font-medium truncate">{label}</span>
        <span className="font-mono text-[10px] text-muted-foreground shrink-0 ml-3">{open ? "Collapse" : "Edit"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4" data-testid="admin-item-editor">
          {Object.entries(data).filter(([k]) => k !== "id").map(([k, v]) => (
            <div key={k}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                {k.replace(/_/g, " ")}{Array.isArray(item[k]) ? " (comma-separated)" : ""}
              </label>
              {typeof item[k] === "boolean" ? (
                <input type="checkbox" checked={!!v} onChange={(e) => setData({ ...data, [k]: e.target.checked })}
                  data-testid={`admin-field-${k}`} className="h-4 w-4 accent-blue-600" />
              ) : typeof item[k] === "number" ? (
                <input type="number" value={v} onChange={(e) => setData({ ...data, [k]: Number(e.target.value) })}
                  data-testid={`admin-field-${k}`} className={inputCls} />
              ) : LONG_FIELDS.includes(k) || String(v).length > 100 ? (
                <textarea rows={k === "content" ? 12 : 3} value={v} onChange={(e) => setData({ ...data, [k]: e.target.value })}
                  data-testid={`admin-field-${k}`} className={`${inputCls} resize-y font-mono text-xs`} />
              ) : (
                <input value={v} onChange={(e) => setData({ ...data, [k]: e.target.value })}
                  data-testid={`admin-field-${k}`} className={inputCls} />
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={busy} data-testid="admin-item-save"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-60">
              <Save size={13} /> Save
            </button>
            {item.id && (
              <button onClick={del} data-testid="admin-item-delete"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-red-500/40 text-red-500 text-xs font-semibold hover:bg-red-500/10 transition-colors duration-200">
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InboxView = () => {
  const [data, setData] = useState({ contacts: [], subscribers: [] });
  useEffect(() => {
    const t = localStorage.getItem("admin_token");
    ax.get("/admin/submissions", { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-lg font-medium mb-4">Contact Submissions ({data.contacts.length})</h2>
        <div className="space-y-3">
          {data.contacts.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-card p-5 text-sm">
              <p className="font-medium">{c.name} <span className="text-muted-foreground">· {c.email} · {c.company || "-"}</span></p>
              <p className="mt-2 text-muted-foreground whitespace-pre-wrap">{c.message}</p>
              <p className="mt-2 font-mono text-[10px] text-muted-foreground">{c.created_at}</p>
            </div>
          ))}
          {data.contacts.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
        </div>
      </div>
      <div>
        <h2 className="font-display text-lg font-medium mb-4">Newsletter Subscribers ({data.subscribers.length})</h2>
        <div className="flex flex-wrap gap-2">
          {data.subscribers.map((s) => (
            <span key={s.id} className="px-3 py-1.5 rounded-full border border-border text-xs">{s.email}</span>
          ))}
          {data.subscribers.length === 0 && <p className="text-sm text-muted-foreground">No subscribers yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [active, setActive] = useState("projects");
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem("admin_token");
    ax.get("/auth/me", { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const load = () => {
    if (active === "submissions") return;
    setAdding(false);
    axios.get(`${API}/content`).then(({ data }) => setItems(data[active] || []));
  };

  useEffect(load, [active, user]);

  const logout = async () => {
    try { await ax.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }); } catch {}
    localStorage.removeItem("admin_token");
    setUser(false);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex bg-background text-foreground">
      <aside className="w-56 shrink-0 border-r border-border bg-card p-5 flex flex-col">
        <p className="font-display font-semibold tracking-tight mb-8">CMS<span className="text-secondary">.</span></p>
        <nav className="space-y-1 flex-1">
          {COLLECTIONS.map((c) => (
            <button key={c.key} onClick={() => setActive(c.key)}
              data-testid={`admin-nav-${c.key}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${active === c.key ? "bg-secondary/15 text-secondary" : "text-muted-foreground hover:text-foreground"}`}>
              {c.key === "submissions" ? <span className="inline-flex items-center gap-2"><Inbox size={13} /> {c.label}</span> : c.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-red-500 transition-colors duration-150">
          <LogOut size={13} /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-8 overflow-y-auto max-h-screen">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl font-medium tracking-tight capitalize">{COLLECTIONS.find((c) => c.key === active)?.label}</h1>
          {active !== "submissions" && (
            <button onClick={() => setAdding(!adding)} data-testid="admin-add-item"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold hover:opacity-90 transition-opacity duration-200">
              <Plus size={13} /> Add item
            </button>
          )}
        </div>

        {active === "submissions" ? (
          <InboxView />
        ) : (
          <div className="space-y-4 max-w-3xl">
            {adding && <ItemEditor collection={active} item={FIELD_HINTS[active] || {}} onSaved={load} onDeleted={load} />}
            {items.map((item) => (
              <ItemEditor key={item.id} collection={active} item={item} onSaved={load} onDeleted={load} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
